/** DevExtreme Pager defaults for server-side report grids. */
export const REPORT_PAGE_SIZES = [20, 50, 100, 200];

/** Stable reference — never recreate per render (breaks DX remote totalCount). */
export const reportPagerProps = Object.freeze({
  visible: true,
  showPageSizeSelector: true,
  showInfo: true,
  showNavigationButtons: true,
  displayMode: 'full',
  allowedPageSizes: REPORT_PAGE_SIZES,
  infoText: 'Sayfa {0} / {1} ({2} kayıt)',
});

/**
 * Stable reference — MUST be the same object identity across React re-renders.
 *
 * If a new object is passed each render (e.g. after setState in CustomStore.load),
 * DX re-inits the data adapter and drops totalCount → pager shows "1 / 1 (pageSize)".
 *
 * filtering/sorting must be true even if the API ignores them: with sorting:false,
 * DX disables remote paging when a sort is present in storeLoadOptions.
 */
export const REPORT_REMOTE_PAGING_OPS = Object.freeze({
  paging: true,
  filtering: true,
  sorting: true,
  grouping: false,
  summary: false,
});

/** @deprecated use REPORT_REMOTE_PAGING_OPS constant */
export function reportRemotePagingOps() {
  return REPORT_REMOTE_PAGING_OPS;
}

export const REPORT_PAGING_50 = Object.freeze({
  enabled: true,
  defaultPageSize: 50,
});

export const REPORT_PAGING_15 = Object.freeze({
  enabled: true,
  defaultPageSize: 15,
});

/** @deprecated use REPORT_PAGING_50 / REPORT_PAGING_15 */
export function reportPagingProps(defaultPageSize = 50) {
  if (defaultPageSize === 15) return REPORT_PAGING_15;
  if (defaultPageSize === 50) return REPORT_PAGING_50;
  return { enabled: true, defaultPageSize };
}

function pickRawTotalCount(root, payload) {
  if (root && typeof root === 'object') {
    if (Object.prototype.hasOwnProperty.call(root, 'totalCount')) return root.totalCount;
    if (Object.prototype.hasOwnProperty.call(root, 'TOTALCOUNT')) return root.TOTALCOUNT;
    if (Object.prototype.hasOwnProperty.call(root, 'total_count')) return root.total_count;
    if (Object.prototype.hasOwnProperty.call(root, 'TotalCount')) return root.TotalCount;
  }
  if (payload && typeof payload === 'object' && payload !== root) {
    if (Object.prototype.hasOwnProperty.call(payload, 'totalCount')) return payload.totalCount;
    if (Object.prototype.hasOwnProperty.call(payload, 'TOTALCOUNT')) return payload.TOTALCOUNT;
    if (Object.prototype.hasOwnProperty.call(payload, 'total_count')) return payload.total_count;
    if (Object.prototype.hasOwnProperty.call(payload, 'TotalCount')) return payload.TotalCount;
  }
  return undefined;
}

/**
 * Normalize API / CustomStore page payload.
 * When the API sends totalCount (including 0), NEVER substitute data.length.
 */
export function normalizeReportPageResult(payload) {
  if (Array.isArray(payload)) {
    return { data: payload, totalCount: payload.length };
  }
  const root =
    payload
    && typeof payload === 'object'
    && payload.data
    && typeof payload.data === 'object'
    && !Array.isArray(payload.data)
    && (payload.data.totalCount != null
      || payload.data.TOTALCOUNT != null
      || Array.isArray(payload.data.data))
      ? payload.data
      : payload;
  const data = Array.isArray(root?.data) ? root.data : [];
  const raw = pickRawTotalCount(root, payload);
  if (raw !== undefined && raw !== null && raw !== '') {
    const totalCount = Number(raw);
    if (Number.isFinite(totalCount) && totalCount >= 0) {
      return { data, totalCount };
    }
  }
  return { data, totalCount: data.length };
}

/**
 * Defer until AFTER DevExtreme's load promise continuation applies totalCount.
 * setTimeout(0) can still race; 50ms is safer without feeling laggy.
 */
export function scheduleAfterDxLoad(fn) {
  if (typeof fn !== 'function') return;
  setTimeout(fn, 50);
}

/** Format the toolbar "Toplam" label. */
export function formatReportTotalLabel(totalCount) {
  return `Toplam: ${Number(totalCount || 0).toLocaleString('tr-TR')} kayıt`;
}

/**
 * Write total into a DOM node (ref.current). Avoids React setState so DataGrid
 * does not re-render and re-init the DX data adapter (which drops totalCount → 50).
 */
export function writeReportTotalLabel(elOrRef, totalCount) {
  const el = elOrRef && typeof elOrRef === 'object' && 'current' in elOrRef
    ? elOrRef.current
    : elOrRef;
  if (!el) return;
  el.textContent = formatReportTotalLabel(totalCount);
}

/**
 * After CustomStore.load sets lastTotalRef and returns { data, totalCount },
 * publish the API total to the toolbar WITHOUT setState during load.
 */
export function publishReportTotalAfterLoad(lastTotalRef, applyTotal) {
  if (typeof applyTotal !== 'function' || !lastTotalRef) return;
  scheduleAfterDxLoad(() => {
    const tc = Number(lastTotalRef.current);
    if (!Number.isFinite(tc) || tc < 0) return;
    applyTotal(tc);
  });
}

/** Read totalCount from a DataGrid instance / event (onContentReady). */
export function readGridDataSourceTotalCount(componentOrEvent) {
  const component = componentOrEvent?.component ?? componentOrEvent;
  const ds = component?.getDataSource?.();
  if (!ds || typeof ds.totalCount !== 'function') return null;
  const n = Number(ds.totalCount());
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Toolbar sync: ALWAYS prefer lastTotalRef (API total from CustomStore.load).
 * Never trust grid.totalCount() alone — after a React/DX race it often equals
 * page size (50) and must not overwrite the real API total (e.g. 407).
 */
export function syncReportToolbarTotalFromGrid(_componentOrEvent, applyTotal, lastTotalRef) {
  if (typeof applyTotal !== 'function') return;
  if (lastTotalRef != null && lastTotalRef.current != null) {
    const apiTc = Number(lastTotalRef.current);
    if (Number.isFinite(apiTc) && apiTc >= 0) {
      applyTotal(apiTc);
      return;
    }
  }
}
